import { useMemo, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Search,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  BookOpen,
  Users,
  CalendarCheck,
  CalendarDays,
  IndianRupee,
  Layers,
  FileCheck,
  BarChart3,
  Settings,
  ShieldCheck,
  ClipboardList,
  Headphones,
  Lightbulb,
  Rocket,
  Play,
  FileText,
  Sparkles,
} from "lucide-react";
import "../../styles/Help.css";

const POPULAR_SEARCHES = [
  "payroll process",
  "leave balance",
  "attendance import",
  "payslip",
  "salary components",
  "PF",
  "ESI",
];

const FEATURED_MODULE = {
  key: "getting-started",
  label: "Getting Started",
  desc: "Setup guide and first steps — Learn how to set up your organization, add employees, configure salary structures, and process your first payroll.",
  Icon: Rocket,
  tint: "ic-blue",
  path: "/help/getting-started",
};

const HELP_MODULES = [
  { key: "employees",  label: "Employee Module",     desc: "Manage employees and organization",                Icon: Users,         tint: "ic-violet",  path: "/help/employees" },
  { key: "attendance", label: "Attendance",           desc: "Track attendance and working hours",               Icon: CalendarCheck, tint: "ic-green",   path: "/help/attendance" },
  { key: "leave",      label: "Leave Management",    desc: "Leave types, allocation and approval",             Icon: CalendarDays,  tint: "ic-teal",    path: "/help/leave" },
  { key: "salary",     label: "Salary & Components", desc: "Setup salary structure, allowances & deductions",  Icon: IndianRupee,   tint: "ic-amber",   path: "/help/salary" },
  { key: "payroll",    label: "Payroll Processing",  desc: "Process, review and finalize payroll",             Icon: FileCheck,     tint: "ic-rose",    path: "/help/payroll" },
  { key: "reports",    label: "Reports",              desc: "View, download and analyse reports",              Icon: BarChart3,     tint: "ic-sky",     path: "/help/reports" },
  { key: "settings",   label: "Settings Guide",      desc: "System settings and configurations",               Icon: Settings,      tint: "ic-slate",   path: "/help/settings" },
  { key: "compliance", label: "Compliance",           desc: "PF, ESI, TDS and statutory setup",                Icon: ShieldCheck,   tint: "ic-indigo",  path: "/help/compliance" },
  { key: "structure",  label: "Company Structure",    desc: "Departments, branches & designations",            Icon: Layers,        tint: "ic-emerald", path: "/help/company-structure" },
];

const WORKFLOW_STEPS = [
  { num: 1, label: "Payroll Period",    desc: "Create payroll period" },
  { num: 2, label: "Validate Data",     desc: "Validate attendance, leave & salary data" },
  { num: 3, label: "Process Payroll",   desc: "Generate payroll for all employees" },
  { num: 4, label: "Review Payroll",    desc: "Review summary and make changes" },
  { num: 5, label: "Finalize Payroll",  desc: "Finalize and lock payroll" },
  { num: 6, label: "Payslips",          desc: "Generate and download payslips" },
];

const FAQ_DATA = [
  { q: "How do I process payroll?",                  a: "Navigate to Payroll > Process Payroll. Select the payroll period, verify attendance and leave data, then click Process. Review the summary and finalize when ready." },
  { q: "Why is my employee not showing in payroll?", a: "Ensure the employee is marked as Active, has a valid salary structure assigned, and their joining date falls within the payroll period. Check Settings > Salary Structure for missing configurations." },
  { q: "How to import attendance data?",             a: "Go to Attendance > Import. Download the template, fill in your attendance data, and upload the completed file. The system will validate and show any errors before importing." },
  { q: "How are leave balances calculated?",         a: "Leave balances are calculated based on your leave policy configuration. Go to Leave > Settings to view allocation rules, carry-forward policies, and accrual settings." },
  { q: "How to generate payslips?",                  a: "After finalizing payroll, go to Payroll > Payslips. Select the period, choose employees, and click Generate. You can download individual or bulk payslips as PDF." },
];

const QUICK_LINKS = [
  { key: "videos",   label: "Video Tutorials",  desc: "Watch step-by-step videos",      Icon: Play,      tint: "ic-blue" },
  { key: "best",     label: "Best Practices",   desc: "Learn tips to work efficiently",  Icon: Lightbulb, tint: "ic-rose" },
  { key: "release",  label: "Release Notes",    desc: "Stay updated with new features",  Icon: FileText,  tint: "ic-green" },
  { key: "whatsnew", label: "What's New?",       desc: "Explore latest updates",          Icon: Sparkles,  tint: "ic-amber" },
];

export default function HelpAccordion() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [openFaq, setOpenFaq] = useState(null);
  const resultsRef = useRef(null);

  const lc = (s) => (s || "").toLowerCase();
  const q = search.trim().toLowerCase();

  const filteredModules = useMemo(() => {
    if (!q) return HELP_MODULES;
    return HELP_MODULES.filter(
      (mod) => lc(mod.label).includes(q) || lc(mod.desc).includes(q) || lc(mod.key).includes(q)
    );
  }, [q]);

  const showFeatured = useMemo(() => {
    if (!q) return true;
    return lc(FEATURED_MODULE.label).includes(q) || lc(FEATURED_MODULE.desc).includes(q) || lc(FEATURED_MODULE.key).includes(q);
  }, [q]);

  const filteredFaqs = useMemo(() => {
    if (!q) return FAQ_DATA;
    return FAQ_DATA.filter((f) => lc(f.q).includes(q) || lc(f.a).includes(q));
  }, [q]);

  const handleSearch = useCallback((e) => {
    e.preventDefault();
    if (q && resultsRef.current) resultsRef.current.scrollIntoView({ behavior: "smooth" });
  }, [q]);

  const handlePopularClick = useCallback((tag) => {
    setSearch(tag);
  }, []);

  const FeaturedIcon = FEATURED_MODULE.Icon;

  return (
    <div className="brix-page">
      {/* ── Hero (compact) ── */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <h1>Help Center</h1>
          <p className="hs-hero-sub">
            Learn how to use BrixiGo HRMS & Payroll. Browse documentation, guides, and FAQs.
          </p>

          <form className="hs-search-wrap" onSubmit={handleSearch}>
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input
                type="text"
                className="hs-search-input"
                placeholder="Search — e.g. attendance, employee, payroll, leave..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>

          <div className="hs-popular">
            <span className="hs-popular-label">Popular Searches:</span>
            {POPULAR_SEARCHES.map((tag) => (
              <button
                key={tag}
                type="button"
                className="hs-popular-tag"
                onClick={() => handlePopularClick(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        <div className="hs-hero-illustration">
          <div className="hs-hero-illus-inner">
            <HelpCircle size={56} strokeWidth={1.2} />
            <div className="hs-illus-float hs-illus-float-1">
              <BookOpen size={13} /> Guides
            </div>
            <div className="hs-illus-float hs-illus-float-2">
              <Lightbulb size={13} /> Tips
            </div>
            <div className="hs-illus-float hs-illus-float-3">
              <ClipboardList size={13} /> FAQ
            </div>
          </div>
        </div>
      </div>

      {/* ── Browse Topics ── */}
      <div style={{ marginBottom: 22 }}>
        <div className="hs-section-head">
          <h2 className="hs-section-title">Browse Topics</h2>
          <p className="hs-section-sub">Explore documentation and guides for every module</p>
        </div>

        <div ref={resultsRef}>
          {/* Featured: Getting Started (full-width) */}
          {showFeatured && (
            <div
              className="hs-featured-card"
              role="button"
              tabIndex={0}
              onClick={() => navigate(FEATURED_MODULE.path)}
              onKeyDown={(e) => { if (e.key === "Enter") navigate(FEATURED_MODULE.path); }}
            >
              <div className={`hs-featured-icon ${FEATURED_MODULE.tint}`}>
                <FeaturedIcon size={28} strokeWidth={1.8} />
              </div>
              <div className="hs-featured-body">
                <div className="hs-featured-label">{FEATURED_MODULE.label}</div>
                <div className="hs-featured-desc">{FEATURED_MODULE.desc}</div>
              </div>
              <div className="hs-featured-action">
                Get Started <ChevronRight size={16} />
              </div>
            </div>
          )}

          {/* 3×3 Module Grid */}
          {q && !showFeatured && filteredModules.length === 0 && (
            <div style={{ textAlign: "center", padding: "24px 16px", color: "#64748b", fontSize: "0.85rem" }}>
              No help modules found for &ldquo;<strong>{search}</strong>&rdquo;. Try a different keyword.
            </div>
          )}
          <div className="hs-module-grid-3col">
            {filteredModules.map((mod) => {
              const Icon = mod.Icon;
              return (
                <div
                  key={mod.key}
                  className="hs-module-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(mod.path)}
                  onKeyDown={(e) => { if (e.key === "Enter") navigate(mod.path); }}
                >
                  <div className={`hs-module-icon ${mod.tint}`}>
                    <Icon size={22} strokeWidth={1.8} />
                  </div>
                  <div className="hs-module-name">{mod.label}</div>
                  <div className="hs-module-desc">{mod.desc}</div>
                  <div className="hs-module-link">
                    View Guide <ChevronRight size={14} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Payroll Process Flow ── */}
      <div className="hs-workflow-card">
        <div className="hs-section-head">
          <h2 className="hs-section-title">Payroll Process Flow</h2>
          <p className="hs-section-sub">Understand the complete payroll workflow</p>
        </div>

        <div className="hs-workflow-row">
          {WORKFLOW_STEPS.map((step, idx) => (
            <div key={step.num} style={{ display: "flex", alignItems: "flex-start", flex: 1 }}>
              <div className="hs-workflow-step">
                <div className="hs-workflow-num wf-blue">{step.num}</div>
                <div className="hs-workflow-step-label">{step.label}</div>
                <div className="hs-workflow-step-desc">{step.desc}</div>
              </div>
              {idx < WORKFLOW_STEPS.length - 1 && (
                <div className="hs-workflow-arrow">
                  <ArrowRight size={18} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── FAQ + Support Center CTA ── */}
      <div className="hs-bottom-grid">
        {/* Left — FAQ */}
        <div className="hs-faq-card">
          <div className="hs-faq-header">
            <div>
              <h2 className="hs-section-title" style={{ marginBottom: 0 }}>Frequently Asked Questions</h2>
            </div>
            <button type="button" className="hs-faq-link">View all</button>
          </div>

          <div className="hs-faq-list">
            {filteredFaqs.length === 0 && q && (
              <div style={{ textAlign: "center", padding: "16px", color: "#94a3b8", fontSize: "0.82rem" }}>
                No FAQs match your search.
              </div>
            )}
            {filteredFaqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div key={faq.q} className="hs-faq-item">
                  <button
                    type="button"
                    className="hs-faq-btn"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                  >
                    <span className="hs-faq-q">{faq.q}</span>
                    <ChevronDown size={16} className={`hs-faq-chevron${isOpen ? " open" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="hs-faq-answer">{faq.a}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right — Quick Help + Support Center CTA */}
        <div>
          <div className="hs-quick-card">
            <div className="hs-section-head">
              <h2 className="hs-section-title">Quick Help</h2>
            </div>
            <div className="hs-quick-grid">
              {QUICK_LINKS.map((link) => {
                const Icon = link.Icon;
                return (
                  <div key={link.key} className="hs-quick-item" role="button" tabIndex={0}>
                    <div className={`hs-quick-icon ${link.tint}`}>
                      <Icon size={16} strokeWidth={2} />
                    </div>
                    <div>
                      <div className="hs-quick-label">{link.label}</div>
                      <div className="hs-quick-sub">{link.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <div className="hs-support-card" style={{ padding: 20, textAlign: "center" }}>
              <Headphones size={28} style={{ color: "#2563eb", marginBottom: 10 }} />
              <div style={{ fontSize: "0.92rem", fontWeight: 800, color: "#0f172a", marginBottom: 4 }}>
                Need Immediate Help?
              </div>
              <div style={{ fontSize: "0.78rem", fontWeight: 500, color: "#64748b", marginBottom: 14 }}>
                Visit our Support Center for troubleshooting and issue resolution.
              </div>
              <button
                type="button"
                className="hs-onboarding-btn"
                style={{ width: "100%", justifyContent: "center", background: "linear-gradient(135deg, #1a437d, #2563eb)", color: "#fff", border: "none" }}
                onClick={() => navigate("/support")}
              >
                Go to Support Center <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
